'use client';

import React, { useEffect, useState } from 'react';
// import DataTable from "../common/DataTable"
import { getRoles, deleteRole } from '../../../../services/RolesServices.js';
// import PageTitle from "../common/PageTitle"
import Swal from 'sweetalert2';
import { toast } from '../../common/Toast/toast';
import PageTitle from '../../common/PageTitle.jsx';
import DataTable from '../../common/CustomDataTable.jsx';
import SettingsFormModal from '../../common/SettingsFormModal.jsx';
import AddRole from './AddRoles.jsx';
import { confirm } from '../../common/Confirm/confirm';

const Role = () => {
  const [data, setData] = useState([]);
  const [totalRecords, setTotalRecords] = useState(100);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  /* The add/edit dialog. null is closed; {} is a new role; { recordId }
     edits that one. The links this replaced pointed at /setting/addroles
     and /setting/editroles/:id, neither of which is a route -- the real
     ones are add-Roles and edit-Roles -- so both add and edit landed on
     the 404 page. */
  const [formModal, setFormModal] = useState(null);
  const recordsPerPage = 10;

  const fetchData = async (page = 1, search = '') => {
    try {
      const res = await getRoles({
        limit: recordsPerPage,
        page,
        search,
        includeTotal: true,
        searchField: 'name',
      });

      const result = {
        records: res.data || [], // Accessing the 'data' key from API response
        totalCount: res.pagination?.total || 0, // Using the total from pagination for accurate count
      };

      setData(result.records);
      setTotalRecords(Number(result.totalCount || result.records.length || 0));
      setCurrentPage(page);
    } catch (error) {
      console.error('Error fetching Roles:', error);
    }
  };

  useEffect(() => {
    fetchData(currentPage, searchTerm);
    // eslint-disable-next-line
  }, [currentPage, searchTerm]);

  const handleSearch = (search) => {
    const term = typeof search === 'string' ? search : search?.term || '';
    setSearchTerm(String(term || ''));
    setCurrentPage(1);
  };

  const handleDelete = async (id) => {
    confirm({
      title: 'Delete Confirmation',
      tone: 'danger',
      text: 'Are you sure you want to delete this Role?',
      confirmButtonText: 'Yes, Delete it!',
      cancelButtonText: 'No, Cancel',
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await deleteRole(id);
          setData((prevData) => prevData.filter((role) => role.id !== id));

          toast.success('The Role has been deleted successfully.');
        } catch (error) {
          toast.error('Failed to delete the Role. Please try again.');
        }
      }
    });
  };

  const handleView = (id) => {
    const role = data.find((item) => item.id === id);

    if (!role) {
      toast.error('Role not found.');
      return;
    }

    Swal.fire({
      html: `
        <p><strong>Label:</strong> ${role.label || 'N/A'}</p>
        <p><strong>Value:</strong> ${role.value || 'N/A'}</p>
        <p><strong>Created At:</strong> ${
          role.createdAt ? new Date(role.createdAt).toLocaleDateString() : 'N/A'
        }</p>
        <p><strong>Updated At:</strong> ${
          role.updatedAt ? new Date(role.updatedAt).toLocaleDateString() : 'N/A'
        }</p>
      `,
      icon: 'info',
      iconColor: '#ff800d',
      confirmButtonText: 'Close',
      confirmButtonColor: '#ff2c41',
      backdrop: 'rgba(70, 69, 69, 0.7)',
    });
  };

  // Calculate indexed data with proper serial numbers
  const indexedData = data.map((item, index) => ({
    ...item,
    serialNumber: (currentPage - 1) * recordsPerPage + index + 1,
  }));

  const headers = [
    {
      title: 'SN',
      prop: 'serialNumber',
      sortable: false,
      cell: (row) => <>{row.serialNumber}</>,
    },
    { title: 'Label', prop: 'label', sortable: true, filterable: true },
    { title: 'Value', prop: 'value', sortable: true, filterable: true },
    {
      title: 'Action',
      prop: 'action',
      cell: (row) => (
        <>
          <button
            type="button"
            onClick={() => setFormModal({ recordId: String(row.id) })}
            className="btn btn-primary shadow btn-xs sharp me-2"
            aria-label={`Edit ${row.label || 'this role'}`}
          >
            <i className="fa fa-pencil" />
          </button>
          <button
            className="btn btn-danger shadow btn-xs sharp me-2"
            onClick={() => handleDelete(row.id)}
          >
            <i className="fa fa-trash" />
          </button>
          <button
            className="btn btn-success shadow btn-xs sharp"
            onClick={() => handleView(row.id)}
          >
            <i className="fa fa-eye" />
          </button>
        </>
      ),
    },
  ];

  return (
    <>
      <PageTitle pageTitle="Roles List" />

      <DataTable
        data={indexedData}
        headers={headers}
        totalRecords={totalRecords}
        currentPage={currentPage}
        recordsPerPage={recordsPerPage}
        onPageChange={setCurrentPage}
        onSearch={handleSearch}
        serverSide={true}
        addButtonText="Add"
        onAddClick={() => setFormModal({})}
      />

      <SettingsFormModal
        open={Boolean(formModal)}
        eyebrow={formModal?.recordId ? 'Edit role' : 'New role'}
        title={formModal?.recordId ? 'Edit role' : 'Add role'}
        subtitle="A role is the label a user account carries; apps are granted to roles."
        labelledBy="role-form-title"
        onClose={() => setFormModal(null)}
      >
        <AddRole
          embedded
          recordId={formModal?.recordId}
          onCancel={() => setFormModal(null)}
          onDone={() => {
            // Saved: the dialog swaps the form for the done panel and closes
            // itself five seconds later (SettingsFormModal).
            fetchData(currentPage, searchTerm);
          }}
        />
      </SettingsFormModal>
    </>
  );
};

export default Role;
